import { eventIterator, oc } from "@orpc/contract";
import { z } from "zod";
import { AgentStreamEventSchema } from "./ai/chat-agent.contract";

export const PROJECT_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 * 1024;
export const PROJECT_AGENT_MAX_INPUT_BYTES = 1024 * 1024 * 1024;

export const ProjectEntryKindSchema = z.enum(["folder", "file"]);
export const ProjectChangeOperationSchema = z.enum(["create", "replace"]);

export const ProjectSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  fileCount: z.number().int().nonnegative(),
  createdBy: z.object({ id: z.uuid(), name: z.string() }),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const ProjectEntrySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  parentEntryId: z.uuid().nullable(),
  kind: ProjectEntryKindSchema,
  name: z.string(),
  path: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  currentVersionId: z.uuid().nullable(),
  currentVersionNumber: z.number().int().positive().nullable(),
  stagedChange: ProjectChangeOperationSchema.nullable(),
  updatedBy: z.object({ id: z.uuid(), name: z.string() }),
  updatedAt: z.coerce.date(),
});

export const ProjectVersionSchema = z.object({
  id: z.uuid(),
  versionNumber: z.number().int().positive(),
  source: z.enum(["user_upload", "nami", "restore"]),
  sizeBytes: z.number().int().nonnegative(),
  mimeType: z.string().nullable(),
  createdBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  createdAt: z.coerce.date(),
});

export const ProjectChangesetItemSchema = z.object({
  id: z.uuid(),
  path: z.string(),
  operation: ProjectChangeOperationSchema,
  baseVersionId: z.uuid().nullable(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative(),
  summary: z.string().nullable(),
  previewUrl: z.url().nullable(),
});

export const ProjectChangesetSchema = z.object({
  id: z.uuid(),
  status: z.enum(["open", "published", "discarded"]),
  items: z.array(ProjectChangesetItemSchema),
  updatedAt: z.coerce.date(),
});

const ProjectIdSchema = z.object({ projectId: z.uuid() });
const EntryIdSchema = z.object({ projectId: z.uuid(), entryId: z.uuid() });

export const ProjectChatMessageSchema = z.object({
  id: z.uuid(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  author: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  createdAt: z.coerce.date(),
});

export const projectsContracts = {
  list: oc
    .input(z.object({ search: z.string().trim().max(255).optional() }))
    .output(z.array(ProjectSummarySchema))
    .route({
      method: "GET",
      summary: "List projects",
      description:
        "Lists the organization's file projects, optionally filtered by a name search, with description, file count, creator and last-updated time.",
    }),
  create: oc
    .input(
      z.object({
        name: z.string().trim().min(1).max(255),
        description: z.string().trim().max(10_000).nullable().optional(),
      }),
    )
    .output(ProjectSummarySchema)
    .route({
      method: "POST",
      summary: "Create a project",
      description:
        "Creates a new file project in the organization with a name and optional description, owned by the current user.",
    }),
  get: oc.input(ProjectIdSchema).output(ProjectSummarySchema).route({
    method: "GET",
    summary: "Get a project",
    description:
      "Returns one project's name, description, file count, creator and timestamps by project ID.",
  }),
  update: oc
    .input(
      ProjectIdSchema.extend({
        name: z.string().trim().min(1).max(255).optional(),
        description: z.string().trim().max(10_000).nullable().optional(),
      }),
    )
    .output(ProjectSummarySchema)
    .route({
      method: "PUT",
      summary: "Update a project",
      description:
        "Renames a project or changes its description; returns the updated project.",
    }),
  entries: {
    list: oc
      .input(
        ProjectIdSchema.extend({
          parentEntryId: z.uuid().nullable().optional(),
        }),
      )
      .output(z.array(ProjectEntrySchema))
      .route({
        method: "GET",
        summary: "List project files and folders",
        description:
          "Lists the files and folders directly inside a project folder, or at the project root when no parent folder is given, with version and pending-change status.",
      }),
    search: oc
      .input(
        ProjectIdSchema.extend({
          query: z.string().trim().min(1).max(255),
          kind: ProjectEntryKindSchema.optional(),
          mimeType: z.string().trim().max(255).optional(),
          limit: z.number().int().min(1).max(100).default(50),
        }),
      )
      .output(z.array(ProjectEntrySchema))
      .route({
        method: "GET",
        summary: "Search project files by path",
        description:
          "Finds files and folders in a project whose path contains the query text, optionally filtered by kind or MIME type, up to a limit.",
      }),
    createFolder: oc
      .input(
        ProjectIdSchema.extend({
          parentEntryId: z.uuid().nullable().optional(),
          name: z.string().trim().min(1).max(255),
        }),
      )
      .output(ProjectEntrySchema)
      .route({
        method: "POST",
        summary: "Create a project folder",
        description:
          "Creates a new folder in a project, under a parent folder or at the root, and returns the new entry.",
      }),
    rename: oc
      .input(EntryIdSchema.extend({ name: z.string().trim().min(1).max(255) }))
      .output(ProjectEntrySchema)
      .route({
        method: "PUT",
        summary: "Rename a project file or folder",
        description:
          "Changes the name of a file or folder in a project and returns the updated entry.",
      }),
    move: oc
      .input(EntryIdSchema.extend({ parentEntryId: z.uuid().nullable() }))
      .output(ProjectEntrySchema)
      .route({
        method: "PUT",
        summary: "Move a project file or folder",
        description:
          "Moves a file or folder to another parent folder in the same project, or to the root when no parent is given.",
      }),
  },
  uploads: {
    create: oc
      .input(
        ProjectIdSchema.extend({
          parentEntryId: z.uuid().nullable().optional(),
          fileName: z.string().trim().min(1).max(255),
          contentType: z.string().trim().min(1).max(255),
          sizeBytes: z
            .number()
            .int()
            .nonnegative()
            .max(PROJECT_MAX_FILE_SIZE_BYTES),
        }),
      )
      .output(
        z.object({
          uploadId: z.uuid(),
          mode: z.enum(["single", "multipart"]),
          storageUploadId: z.string().nullable(),
          partSizeBytes: z.number().int().positive().nullable(),
          uploadUrl: z.url().nullable(),
          partUrls: z.array(
            z.object({ partNumber: z.number().int().positive(), url: z.url() }),
          ),
        }),
      )
      .route({
        method: "POST",
        summary: "Start a project file upload",
        description:
          "Begins uploading a file into a project folder; returns a signed upload URL, or multipart part URLs for large files up to 5 GB.",
      }),
    signParts: oc
      .input(
        z.object({
          uploadId: z.uuid(),
          partNumbers: z
            .array(z.number().int().min(1).max(10_000))
            .min(1)
            .max(100),
        }),
      )
      .output(
        z.array(
          z.object({ partNumber: z.number().int().positive(), url: z.url() }),
        ),
      )
      .route({
        method: "POST",
        summary: "Sign project upload parts",
        description:
          "Returns signed URLs for additional parts of an in-progress multipart project file upload, up to 100 parts per call.",
      }),
    complete: oc
      .input(
        z.object({
          uploadId: z.uuid(),
          parts: z
            .array(
              z.object({
                partNumber: z.number().int().min(1).max(10_000),
                etag: z.string().min(1),
              }),
            )
            .max(10_000),
        }),
      )
      .output(ProjectEntrySchema)
      .route({
        method: "POST",
        summary: "Complete a project file upload",
        description:
          "Finishes an upload from its uploaded parts, adding the file to the project as a new file or a new version, and returns the entry.",
      }),
    abort: oc
      .input(z.object({ uploadId: z.uuid() }))
      .output(z.object({ success: z.literal(true) }))
      .route({
        method: "POST",
        summary: "Cancel a project file upload",
        description:
          "Aborts a pending project upload, discarding any uploaded multipart parts in storage and marking the upload aborted.",
      }),
  },
  files: {
    getPreview: oc
      .input(EntryIdSchema)
      .output(
        z.object({
          mode: z.enum(["pdf", "image", "video", "audio", "text", "download"]),
          url: z.url(),
          mimeType: z.string().nullable(),
          truncated: z.boolean(),
        }),
      )
      .route({
        method: "GET",
        summary: "Get a project file preview",
        description:
          "Returns a temporary URL and preview mode (PDF, image, video, audio, text or download-only) for the current version of a project file.",
      }),
    getDownload: oc
      .input(EntryIdSchema)
      .output(z.object({ url: z.url() }))
      .route({
        method: "GET",
        summary: "Get a project file download link",
        description:
          "Returns a temporary download URL for the current version of a project file.",
      }),
    listVersions: oc
      .input(EntryIdSchema)
      .output(z.array(ProjectVersionSchema))
      .route({
        method: "GET",
        summary: "List project file versions",
        description:
          "Lists every saved version of a project file with version number, source (user upload, assistant, restore), size and author.",
      }),
    restoreVersion: oc
      .input(EntryIdSchema.extend({ versionId: z.uuid() }))
      .output(ProjectEntrySchema)
      .route({
        method: "POST",
        summary: "Restore a project file version",
        description:
          "Makes an earlier version of a project file current by saving it as a new version; earlier versions are kept.",
      }),
  },
  changesets: {
    getOpen: oc
      .input(ProjectIdSchema)
      .output(ProjectChangesetSchema.nullable())
      .route({
        method: "GET",
        summary: "Get pending assistant changes for a project",
        description:
          "Returns the project's open changeset of assistant-proposed file creations and replacements awaiting review, or null when none is pending.",
      }),
    publish: oc
      .input(ProjectIdSchema.extend({ changesetId: z.uuid() }))
      .output(
        z.object({
          success: z.literal(true),
          publishedCount: z.number().int(),
        }),
      )
      .route({
        method: "POST",
        summary: "Publish pending project changes",
        description:
          "Applies the open changeset's assistant-proposed files to the project as new files or versions, creating folders as needed; returns how many were published.",
      }),
    discard: oc
      .input(ProjectIdSchema.extend({ changesetId: z.uuid() }))
      .output(z.object({ success: z.literal(true) }))
      .route({
        method: "POST",
        summary: "Discard pending project changes",
        description:
          "Rejects the open changeset of assistant-proposed file changes without applying any of them to the project.",
      }),
  },
  chat: {
    history: oc
      .input(ProjectIdSchema)
      .output(z.array(ProjectChatMessageSchema))
      .route({
        method: "GET",
        summary: "Get project assistant chat history",
        description:
          "Returns the user and assistant messages from the project's assistant conversation.",
      }),
    send: oc
      .input(
        ProjectIdSchema.extend({
          content: z.string().trim().min(1).max(100_000),
          selectedEntryIds: z.array(z.uuid()).max(500),
          screenshot: z
            .object({
              imageData: z.string().min(1),
              mimeType: z.enum(["image/jpeg", "image/png"]),
            })
            .optional(),
        }),
      )
      .output(eventIterator(AgentStreamEventSchema))
      .route({
        method: "POST",
        summary: "Ask the project assistant",
        description:
          "Sends a message to the project assistant, which works on the selected files in a sandbox, streams its reply, and stages file changes for review.",
      }),
    cancel: oc
      .input(ProjectIdSchema)
      .output(z.object({ success: z.literal(true) }))
      .route({
        method: "POST",
        summary: "Stop the project assistant",
        description:
          "Cancels the project's running assistant turn and shuts down its sandbox.",
      }),
  },
} as const;

export type ProjectSummary = z.infer<typeof ProjectSummarySchema>;
export type ProjectEntry = z.infer<typeof ProjectEntrySchema>;
export type ProjectVersion = z.infer<typeof ProjectVersionSchema>;
export type ProjectChangeset = z.infer<typeof ProjectChangesetSchema>;
export type ProjectChatMessage = z.infer<typeof ProjectChatMessageSchema>;
